import { useCallback } from 'react';

import {
  clampDateInputParts,
  clampDateToBounds,
  formatDateRange,
  formatSingleDate,
  isCompleteInput,
  isRangePicker,
  parseDateRange,
  parseSingleDate,
} from '../utils';

import type { ChangeEvent } from 'react';
import type { PickerConfig } from '../core/PickerConfig';
import type { DatePickerProps, PeriodPickerProps } from '../types';

interface UseDatePickerInputOptions {
  dateFormat: string;
  onClose: () => void;
  props: DatePickerProps | PeriodPickerProps;
  mask?: string;
  pickerConfig?: PickerConfig;
}

export const useDatePickerInput = (options: UseDatePickerInputOptions) => {
  const { props, dateFormat, mask, onClose, pickerConfig } = options;

  /**
   * 입력 변경 핸들러
   * @returns clamp가 적용된 경우 보정된 입력 문자열, 그 외에는 undefined
   */
  const handleInputChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string,
    ): string | undefined => {
      const currentInputValue = newValue || event.target.value;

      props.onInput?.(currentInputValue);

      // 값이 비어있으면 null로 설정 + invalid 상태 해제
      if (!currentInputValue) {
        if (isRangePicker(props)) {
          props.onChange?.([null, null]);
        } else {
          props.onChange?.(null);
        }
        props.onInvalidInput?.(null);
        return undefined;
      }

      const isComplete = isCompleteInput(currentInputValue, mask);

      const normalizePeriodDate = (date: Date): Date =>
        pickerConfig?.canonicalizeDate(date) ?? date;

      // 기간 Picker는 min/max 역시 period 시작 anchor로 비교한다.
      const applyBoundsClamp = (date: Date): Date => {
        const normalizedDate = normalizePeriodDate(date);
        if (!isComplete || !props.clampDateOutOfRange) return normalizedDate;

        const minDate = props.minDate ? normalizePeriodDate(props.minDate) : props.minDate;
        const maxDate = props.maxDate ? normalizePeriodDate(props.maxDate) : props.maxDate;
        return clampDateToBounds(normalizedDate, minDate, maxDate);
      };

      if (isRangePicker(props)) {
        // Range picker 처리
        const [startDate, endDate] = parseDateRange(currentInputValue, dateFormat);

        if (startDate && endDate) {
          const boundedStart = applyBoundsClamp(startDate);
          const boundedEnd = applyBoundsClamp(endDate);
          props.onChange?.([boundedStart, boundedEnd]);
          props.onInvalidInput?.(null);

          // 완전한 날짜 범위 입력 시 calendar 닫기
          if (isComplete) {
            onClose();
          }

          if (boundedStart !== startDate || boundedEnd !== endDate) {
            return `${formatSingleDate(boundedStart, dateFormat)} ~ ${formatSingleDate(boundedEnd, dateFormat)}`;
          }
        } else if (isComplete) {
          let displayValue = currentInputValue;
          if (props.clampDateOutOfRange) {
            const parts = currentInputValue.split(' ~ ');
            if (parts.length === 2) {
              const clamped = `${clampDateInputParts(parts[0], dateFormat)} ~ ${clampDateInputParts(parts[1], dateFormat)}`;
              if (clamped !== currentInputValue) {
                const [clampedStart, clampedEnd] = parseDateRange(clamped, dateFormat);
                if (clampedStart && clampedEnd) {
                  const boundedStart = applyBoundsClamp(clampedStart);
                  const boundedEnd = applyBoundsClamp(clampedEnd);
                  props.onChange?.([boundedStart, boundedEnd]);
                  props.onInvalidInput?.(null);
                  onClose();
                  return boundedStart === clampedStart && boundedEnd === clampedEnd
                    ? clamped
                    : `${formatSingleDate(boundedStart, dateFormat)} ~ ${formatSingleDate(boundedEnd, dateFormat)}`;
                }
                displayValue = clamped;
              }
            }
          }
          props.onInvalidInput?.(displayValue);
          return displayValue !== currentInputValue ? displayValue : undefined;
        } else {
          // 부분 입력 — 더 이상 invalid 메시지를 노출할 단계 아님
          props.onInvalidInput?.(null);
        }
      } else {
        // Single picker 처리
        const parsedDate = parseSingleDate(currentInputValue, dateFormat);

        if (parsedDate) {
          const boundedDate = applyBoundsClamp(parsedDate);
          props.onChange?.(boundedDate);
          props.onInvalidInput?.(null);

          // 완전한 날짜 입력 시 calendar 닫기
          if (isComplete) {
            onClose();
          }

          if (boundedDate !== parsedDate) {
            return formatSingleDate(boundedDate, dateFormat);
          }
        } else if (isComplete) {
          let displayValue = currentInputValue;
          if (props.clampDateOutOfRange) {
            const clamped = clampDateInputParts(currentInputValue, dateFormat);
            if (clamped !== currentInputValue) {
              const clampedParsed = parseSingleDate(clamped, dateFormat);
              if (clampedParsed) {
                const boundedParsed = applyBoundsClamp(clampedParsed);
                props.onChange?.(boundedParsed);
                props.onInvalidInput?.(null);
                onClose();
                return boundedParsed === clampedParsed
                  ? clamped
                  : formatSingleDate(boundedParsed, dateFormat);
              }
              displayValue = clamped;
            }
          }
          props.onInvalidInput?.(displayValue);
          return displayValue !== currentInputValue ? displayValue : undefined;
        } else {
          props.onInvalidInput?.(null);
        }
      }
      return undefined;
    },
    [props, dateFormat, mask, onClose, pickerConfig],
  );

  /**
   * 현재 선택된 날짜를 포맷팅하여 반환
   */
  const getFormattedValue = useCallback((): string => {
    if (isRangePicker(props)) {
      return formatDateRange(props.startDate, props.endDate, dateFormat);
    }
    return formatSingleDate(props.selected, dateFormat);
  }, [props, dateFormat]);

  return {
    getFormattedValue,
    handleInputChange,
  };
};
